import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Container,
  Link,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom';
import { NumericField, OtaFooter } from '@ota/design-system';
import { ApiError } from '@ota/api-client';
import { signup } from '../api';
import { PublicHeader } from '../components/PublicHeader';

export function SignupPage() {
  const navigate = useNavigate();
  const [search] = useSearchParams();
  const returnTo = search.get('returnTo') ?? '/';

  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const phoneError = phone && !/^[0-9]{10,15}$/.test(phone)
    ? 'Enter a valid 10–15 digit mobile number'
    : null;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (phoneError) return;
    setError(null);
    setBusy(true);
    try {
      await signup({ fullName, email, phone, password });
      navigate(returnTo);
    } catch (err) {
      setError(err instanceof ApiError ? err.body?.message ?? err.message : 'Signup failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <PublicHeader />
      <Container maxWidth="sm" sx={{ py: { xs: 3, sm: 6, md: 8 }, flex: 1, px: { xs: 2, sm: 3 } }}>
        <Card elevation={2}>
          <CardContent sx={{ p: { xs: 2.5, sm: 4, md: 5 } }}>
            <Stack spacing={0.5} sx={{ mb: 4, textAlign: 'center' }}>
              <Typography variant="overline" color="secondary.main">Join OTA</Typography>
              <Typography variant="h4" sx={{ fontSize: { xs: '1.5rem', sm: '2rem' } }}>
                Create your account
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Book hotels in seconds. Mobile number required for OTP login.
              </Typography>
            </Stack>

            <form onSubmit={submit}>
              <Stack spacing={2}>
                <TextField
                  label="Full name"
                  required
                  fullWidth
                  autoFocus
                  autoComplete="name"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                />
                <TextField
                  label="Email address"
                  type="email"
                  required
                  fullWidth
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
                <NumericField
                  label="Mobile number"
                  required
                  fullWidth
                  autoComplete="tel"
                  placeholder="10-digit mobile number"
                  inputProps={{ maxLength: 15 }}
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  error={!!phoneError}
                  helperText={phoneError ?? 'Used for OTP login and booking alerts'}
                />
                <TextField
                  label="Password"
                  type="password"
                  required
                  fullWidth
                  autoComplete="new-password"
                  helperText="At least 6 characters"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
                {error && <Alert severity="error" sx={{ borderRadius: 1 }}>{error}</Alert>}
                <Button
                  type="submit"
                  variant="contained"
                  color="secondary"
                  size="large"
                  fullWidth
                  disabled={busy || !!phoneError || !phone}
                  startIcon={busy ? <CircularProgress size={18} color="inherit" /> : null}
                  sx={{ py: 1.5 }}
                >
                  {busy ? 'Creating account…' : 'Create account'}
                </Button>
              </Stack>
            </form>

            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ display: 'block', mt: 3, textAlign: 'center' }}
            >
              Already have an account?{' '}
              <Link
                component={RouterLink}
                to={`/login${returnTo !== '/' ? `?returnTo=${returnTo}` : ''}`}
              >
                Sign in
              </Link>
            </Typography>
          </CardContent>
        </Card>
      </Container>
      <OtaFooter />
    </Box>
  );
}
